import { GrElement, TOKENS_CSS } from "../core/base";

/**
 * <gr-chart topics="co2/co2,co2/t" labels="CO₂,Temp" window="120"
 *           label="Air quality"></gr-chart>
 *
 * Rolling time-series chart. X axis is wall-clock time (`window` seconds),
 * so data buffered while the tab was backgrounded renders correctly on
 * return — no per-frame assumptions. Series autoscale independently.
 */

const SERIES_COLORS = ["#4ade80", "#60a5fa", "#a78bfa", "#f472b6", "#fbbf24", "#34d399"];

interface Point {
  ts: number;
  value: number;
}

export class GrChart extends GrElement {
  private series = new Map<string, Point[]>();
  private canvas!: HTMLCanvasElement;
  private rafScheduled = false;

  connectedCallback(): void {
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 18px;
          border-radius: var(--_radius);
          background: var(--_bg);
          border: 1px solid var(--_border);
        }
        canvas { width: 100%; height: var(--gr-chart-height, 200px); display: block; }
        .legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 11px; color: var(--_muted); }
        .legend span::before {
          content: "";
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          margin-right: 6px;
          vertical-align: middle;
          background: var(--dot);
        }
      </style>
      <span class="label"></span>
      <canvas></canvas>
      <div class="legend"></div>
    `;
    root.querySelector(".label")!.textContent = this.attr("label");
    this.canvas = root.querySelector("canvas")!;

    const topics = this.topicList();
    const labels = this.attr("labels").split(",").map((s) => s.trim());
    const legend = root.querySelector(".legend")!;
    topics.forEach((topic, i) => {
      this.series.set(topic, []);
      const item = document.createElement("span");
      item.textContent = labels[i] || topic;
      item.style.setProperty("--dot", SERIES_COLORS[i % SERIES_COLORS.length]);
      legend.appendChild(item);
      this.track(
        this.bus.subscribe(topic, (msg) => {
          const num = Number(msg.payload);
          if (Number.isNaN(num)) return;
          this.push(topic, { ts: msg.ts, value: num });
        }),
      );
    });
    this.scheduleDraw();
  }

  private topicList(): string[] {
    const multi = this.attr("topics");
    if (multi) return multi.split(",").map((s) => s.trim()).filter(Boolean);
    return [this.attr("topic")].filter(Boolean);
  }

  private windowMs(): number {
    return this.numAttr("window", 120) * 1000;
  }

  private push(topic: string, point: Point): void {
    const points = this.series.get(topic);
    if (!points) return;
    points.push(point);
    const cutoff = Date.now() - this.windowMs() * 1.1;
    while (points.length > 0 && points[0].ts < cutoff) points.shift();
    this.scheduleDraw();
  }

  private scheduleDraw(): void {
    if (this.rafScheduled) return;
    this.rafScheduled = true;
    requestAnimationFrame(() => {
      this.rafScheduled = false;
      this.draw();
    });
  }

  private draw(): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (width === 0 || height === 0) return;
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const now = Date.now();
    const windowMs = this.windowMs();
    const globalMin = this.getAttribute("min") !== null ? this.numAttr("min", 0) : null;
    const globalMax = this.getAttribute("max") !== null ? this.numAttr("max", 100) : null;

    // Faint horizontal grid.
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const y = (height / 4) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    let seriesIndex = 0;
    for (const points of this.series.values()) {
      const color = SERIES_COLORS[seriesIndex % SERIES_COLORS.length];
      seriesIndex++;
      if (points.length < 2) continue;

      let min = globalMin;
      let max = globalMax;
      if (min === null || max === null) {
        let lo = Infinity;
        let hi = -Infinity;
        for (const p of points) {
          if (p.value < lo) lo = p.value;
          if (p.value > hi) hi = p.value;
        }
        const pad = (hi - lo || 1) * 0.1;
        min = min ?? lo - pad;
        max = max ?? hi + pad;
      }

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.beginPath();
      let started = false;
      for (const p of points) {
        const x = width - ((now - p.ts) / windowMs) * width;
        const y = height - ((p.value - min) / (max - min || 1)) * height;
        if (x < -4) continue;
        if (started) ctx.lineTo(x, y);
        else {
          ctx.moveTo(x, y);
          started = true;
        }
      }
      ctx.stroke();
    }
  }
}

customElements.define("gr-chart", GrChart);
